import React, { useState, useEffect, useMemo } from 'react';
import usuariosService from '../services/usuariosService';
import Loading from '../components/Loading';
import './EtiquetasPage.css';

const COLOR_DEFECTO = '#3B82F6';
const FORM_VACIO = { id: null, nombre: '', color: COLOR_DEFECTO, activa: true };

const mensajeError = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err.error) return err.error;
  if (err.detail) return err.detail;
  const primero = Object.values(err)[0];
  if (Array.isArray(primero) && primero.length) return primero[0];
  return fallback;
};

const EtiquetasPage = () => {
  const [etiquetas, setEtiquetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [form, setForm] = useState(null); // null = modal cerrado

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await usuariosService.getEtiquetas();
      setEtiquetas(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      setError(mensajeError(err, 'Error al cargar las etiquetas'));
    } finally {
      setLoading(false);
    }
  };

  const avisar = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3500);
  };

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return q ? etiquetas.filter(e => e.nombre.toLowerCase().includes(q)) : etiquetas;
  }, [etiquetas, busqueda]);

  const guardar = async (e) => {
    e.preventDefault();
    const nombre = form.nombre.trim();
    if (!nombre) {
      setError('El nombre es obligatorio');
      return;
    }
    try {
      setGuardando(true);
      setError(null);
      const payload = { nombre, color: form.color, activa: form.activa };
      if (form.id) {
        await usuariosService.actualizarEtiqueta(form.id, payload);
        avisar('Etiqueta actualizada. Los posts que la usan ya reflejan el cambio.');
      } else {
        await usuariosService.crearEtiqueta(payload);
        avisar('Etiqueta creada');
      }
      setForm(null);
      await cargar();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo guardar la etiqueta'));
    } finally {
      setGuardando(false);
    }
  };

  const alternarActiva = async (et) => {
    try {
      setError(null);
      await usuariosService.actualizarEtiqueta(et.id, { activa: !et.activa });
      await cargar();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo cambiar el estado'));
    }
  };

  const eliminar = async (et) => {
    if (!window.confirm(`¿Eliminar la etiqueta "${et.nombre}"?`)) return;
    try {
      setError(null);
      await usuariosService.eliminarEtiqueta(et.id);
      avisar('Etiqueta eliminada');
      await cargar();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo eliminar la etiqueta'));
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="influencer-list-container">
      <div className="influencer-header">
        <div className="influencer-header-left">
          <h1 className="influencer-title">Etiquetas</h1>
          <span className="influencer-count">{filtradas.length}</span>
        </div>
        <div className="influencer-header-right">
          <input
            type="text"
            className="il-search-input il-search-header"
            placeholder="Buscar..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <button className="il-btn-primary" onClick={() => { setError(null); setForm({ ...FORM_VACIO }); }}>
            + Tag
          </button>
        </div>
      </div>

      {error && (
        <div className="etiquetas-alert etiquetas-alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Cerrar">×</button>
        </div>
      )}
      {success && (
        <div className="etiquetas-alert etiquetas-alert-success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} aria-label="Cerrar">×</button>
        </div>
      )}

      {filtradas.length === 0 ? (
        <div className="il-empty-state">
          <p>{etiquetas.length === 0 ? 'Aún no hay etiquetas. Crea la primera con "Nueva etiqueta".' : 'Sin resultados.'}</p>
        </div>
      ) : (
        <div className="il-table-container">
          <table className="il-table">
            <thead>
              <tr>
                <th>Etiqueta</th>
                <th>Color</th>
                <th className="num">Posts</th>
                <th className="num">Usuarios restringidos</th>
                <th>Estado</th>
                <th aria-label="Acciones"></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(et => (
                <tr key={et.id} className={et.activa ? '' : 'inactiva'}>
                  <td>
                    <span className="etiqueta-badge" style={{ backgroundColor: et.color }}>{et.nombre}</span>
                  </td>
                  <td><code className="dv-code">{et.color}</code></td>
                  <td className="num">{et.posts_count}</td>
                  <td className="num">{et.usuarios_count}</td>
                  <td>
                    <button
                      className={`etiquetas-estado ${et.activa ? 'on' : 'off'}`}
                      onClick={() => alternarActiva(et)}
                      title={et.activa ? 'Clic para desactivar' : 'Clic para activar'}
                    >
                      {et.activa ? 'Activa' : 'Inactiva'}
                    </button>
                  </td>
                  <td>
                    <div className="il-actions">
                      <button
                        className="il-btn-icon"
                        title="Editar"
                        onClick={() => { setError(null); setForm({ id: et.id, nombre: et.nombre, color: et.color, activa: et.activa }); }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
                      </button>
                      <button
                        className="il-btn-icon danger"
                        onClick={() => eliminar(et)}
                        disabled={et.posts_count > 0}
                        title={et.posts_count > 0 ? 'En uso en posts: desactívala en vez de eliminarla' : 'Eliminar'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
              </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="etiquetas-modal-overlay" onClick={() => !guardando && setForm(null)}>
          <form className="etiquetas-modal" onClick={(e) => e.stopPropagation()} onSubmit={guardar}>
            <h2>{form.id ? 'Editar etiqueta' : 'Nueva etiqueta'}</h2>

            <label>Nombre</label>
            <input
              type="text"
              value={form.nombre}
              maxLength={50}
              autoFocus
              onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder="Ej: Trini"
            />

            <label>Color</label>
            <div className="etiquetas-color-row">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value.toUpperCase() }))}
              />
              <code>{form.color}</code>
              <span className="etiqueta-badge" style={{ backgroundColor: form.color }}>
                {form.nombre.trim() || 'Vista previa'}
              </span>
            </div>

            <label className="etiquetas-check">
              <input
                type="checkbox"
                checked={form.activa}
                onChange={(e) => setForm(prev => ({ ...prev, activa: e.target.checked }))}
              />
              Activa (disponible al crear o editar posts)
            </label>

            {form.id && (
              <p className="etiquetas-hint">
                Al cambiar el nombre o el color se actualizan también los posts y los permisos de usuario que usan esta etiqueta.
              </p>
            )}

            <div className="etiquetas-modal-actions">
              <button type="button" className="etiquetas-btn" onClick={() => setForm(null)} disabled={guardando}>Cancelar</button>
              <button type="submit" className="etiquetas-btn-primary" disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EtiquetasPage;
